import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, DollarSign, TrendingUp, BookOpen, Calendar, Coins, Info, Filter } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MONTH_SHORT = {
  'January': 'Jan', 'February': 'Feb', 'March': 'Mar', 'April': 'Apr',
  'May': 'May', 'June': 'Jun', 'July': 'Jul', 'August': 'Aug',
  'September': 'Sep', 'October': 'Oct', 'November': 'Nov', 'December': 'Dec'
};

const MyEarnings = () => {
  const [loading, setLoading] = useState(true);
  const [earningsData, setEarningsData] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Filter state
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');

  useEffect(() => {
    fetchCurrentUser();
    fetchMyEarnings();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  };

  const fetchMyEarnings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/my/earnings`);
      setEarningsData(response.data);
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
      toast.error('Kon nie verdienste laai nie');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `R${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Get unique years from entries
  const availableYears = useMemo(() => {
    if (!earningsData?.entries || earningsData.entries.length === 0) return [];
    const years = [...new Set(earningsData.entries.map(e => e.year))];
    return years.sort((a, b) => b - a); // Sort descending
  }, [earningsData?.entries]);

  // Always show all 12 months in dropdown
  const availableMonths = useMemo(() => {
    // Always return all months - user should be able to select any month
    return MONTHS;
  }, []);

  // Filter entries based on selection
  const filteredEntries = useMemo(() => {
    if (!earningsData?.entries) return [];
    
    let entries = earningsData.entries;
    
    if (selectedYear !== 'all') {
      entries = entries.filter(e => e.year === parseInt(selectedYear));
    }
    
    if (selectedMonth !== 'all') {
      entries = entries.filter(e => e.month === selectedMonth);
    }
    
    return entries;
  }, [earningsData?.entries, selectedMonth, selectedYear]);

  // Calculate totals for filtered entries
  const calculateTotals = () => {
    if (!filteredEntries || filteredEntries.length === 0) {
      return { totalCoins: 0, totalPayout: 0, totalBooks: 0 };
    }
    
    let totalCoins = 0;
    let totalPayout = 0;
    let allBooks = new Set();
    
    filteredEntries.forEach(entry => {
      totalCoins += entry.coins_spent || 0;
      totalPayout += entry.writer_payout || 0;
      entry.books?.forEach(book => allBooks.add(book));
    });
    
    return { totalCoins, totalPayout, totalBooks: allBooks.size };
  };

  // Prepare chart data - all entries sorted by date
  const chartData = useMemo(() => {
    if (!earningsData?.entries || earningsData.entries.length === 0) return [];
    
    // Sort entries by year and month
    const sorted = [...earningsData.entries].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return MONTHS.indexOf(a.month) - MONTHS.indexOf(b.month);
    });
    
    return sorted.map(entry => ({
      name: `${MONTH_SHORT[entry.month]} ${entry.year}`,
      month: entry.month,
      year: entry.year,
      verdienste: entry.writer_payout,
      munte: entry.coins_spent,
      totaal: entry.total_rand
    }));
  }, [earningsData?.entries]);

  // Reset month when year changes
  const handleYearChange = (value) => {
    setSelectedYear(value);
    setSelectedMonth('all'); // Reset month when year changes
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // For proofreaders/translators - show message
  if (earningsData?.message && currentUser?.role !== 'writer') {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">My Verdienste</h1>
          <p className="text-gray-600">Sien jou voltooide werk en betalings</p>
        </div>

        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-6">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-blue-800">{earningsData.message}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {earningsData.completed_work?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Voltooide Werk
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {earningsData.completed_work.map((book, idx) => (
                  <div key={book.book_id || `book-${idx}`} className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium">{book.boek_naam}</div>
                    <div className="text-sm text-gray-500">
                      deur {book.skrywer_naam} • {book.page_count} bladsye
                    </div>
                    {book.publication_date && (
                      <div className="text-xs text-gray-400 mt-1">
                        Gepubliseer: {new Date(book.publication_date).toLocaleDateString('af-ZA')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">My Verdienste</h1>
        <p className="text-gray-600">Sien jou maandelikse boekverkope en verdienste</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">
                  {selectedMonth !== 'all' || selectedYear !== 'all' ? 'Gefiltreerde' : 'Totale'} Verdienste
                </p>
                <p className="text-3xl font-bold text-green-700">{formatCurrency(totals.totalPayout)}</p>
              </div>
              <DollarSign className="h-10 w-10 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">
                  {selectedMonth !== 'all' || selectedYear !== 'all' ? 'Gefiltreerde' : 'Totale'} Munte
                </p>
                <p className="text-3xl font-bold text-blue-700">{totals.totalCoins.toLocaleString()}</p>
              </div>
              <Coins className="h-10 w-10 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600">Boeke Verkoop</p>
                <p className="text-3xl font-bold text-orange-700">{totals.totalBooks}</p>
              </div>
              <BookOpen className="h-10 w-10 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Settings Info */}
      {earningsData?.settings && (
        <Card className="border-gray-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Info className="h-4 w-4" />
              <span>
                Munt koers: R{earningsData.settings.coin_rate} per munt • 
                Jou deel: {earningsData.settings.writer_pct}% van totale inkomste
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      {earningsData?.entries?.length > 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filter:</span>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Jaar:</label>
                <Select value={selectedYear} onValueChange={handleYearChange}>
                  <SelectTrigger className="w-32" data-testid="year-filter">
                    <SelectValue placeholder="Kies jaar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Jare</SelectItem>
                    {availableYears.map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Maand:</label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-40" data-testid="month-filter">
                    <SelectValue placeholder="Kies maand" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Maande</SelectItem>
                    {availableMonths.map(month => (
                      <SelectItem key={month} value={month}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {(selectedMonth !== 'all' || selectedYear !== 'all') && (
                <button
                  onClick={() => { setSelectedMonth('all'); setSelectedYear('all'); }}
                  className="text-sm text-orange-600 hover:text-orange-700 underline"
                >
                  Wis filter
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Breakdown */}
      {filteredEntries?.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Maandelikse Verdienste
              {(selectedMonth !== 'all' || selectedYear !== 'all') && (
                <span className="text-sm font-normal text-gray-500">
                  ({filteredEntries.length} {filteredEntries.length === 1 ? 'rekord' : 'rekords'})
                </span>
              )}
            </CardTitle>
            <CardDescription>
              'n Oorsig van jou verdienste per maand
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredEntries.map((entry) => (
                <div 
                  key={entry.id} 
                  className="p-4 bg-gray-50 rounded-lg border"
                  data-testid={`earnings-entry-${entry.month}-${entry.year}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{entry.month} {entry.year}</h3>
                      <p className="text-sm text-gray-500">
                        {entry.purchases} aankope • {entry.rentals} huurverdienste
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(entry.writer_payout)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {entry.paid ? (
                          <span className="text-green-600">Betaal</span>
                        ) : (
                          <span className="text-yellow-600">Wag vir betaling</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Books breakdown */}
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm font-medium text-gray-700 mb-2">Boeke:</p>
                    <div className="flex flex-wrap gap-2">
                      {entry.books?.map((book, idx) => (
                        <span 
                          key={idx}
                          className="px-2 py-1 bg-white rounded text-sm border"
                        >
                          {book}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Revenue breakdown */}
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm font-medium text-gray-700 mb-2">Inkomste Verdeling:</p>
                    <div className="grid grid-cols-4 gap-2 text-sm">
                      <div className="text-center p-2 bg-white rounded">
                        <div className="text-gray-500">Munte</div>
                        <div className="font-medium">{entry.coins_spent?.toLocaleString()}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-100 rounded">
                        <div className="text-gray-500">Totaal</div>
                        <div className="font-medium">{formatCurrency(entry.total_rand)}</div>
                      </div>
                      <div className="text-center p-2 bg-orange-50 rounded">
                        <div className="text-orange-600 text-xs">Google/Apple + App</div>
                        <div className="font-medium text-orange-700">
                          {formatCurrency(entry.google_apple_cut + entry.app_cut)}
                        </div>
                      </div>
                      <div className="text-center p-2 bg-green-50 rounded">
                        <div className="text-green-600 text-xs">Jou Verdienste</div>
                        <div className="font-bold text-green-700">{formatCurrency(entry.writer_payout)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : selectedMonth !== 'all' ? (
        // Show zero earnings for selected month with no data
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Maandelikse Verdienste
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-gray-50 rounded-lg border">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-lg">{selectedMonth} {selectedYear !== 'all' ? selectedYear : new Date().getFullYear()}</h3>
                  <p className="text-sm text-gray-500">
                    0 aankope • 0 huurverdienste
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-400">
                    R0.00
                  </div>
                  <div className="text-sm text-gray-400">
                    Geen inkomste
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t text-center text-gray-500">
                <p>Geen boekverkope vir hierdie maand nie.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : earningsData?.entries?.length > 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Filter className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600">Geen resultate vir hierdie filter</h3>
            <p className="text-gray-500 mt-1">
              Probeer 'n ander maand of jaar kies, of wis die filter.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <TrendingUp className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600">Nog geen verdienste nie</h3>
            <p className="text-gray-500 mt-1">
              Sodra jou boeke verkoop word, sal jou verdienste hier verskyn.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Earnings Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Verdienste Geskiedenis
            </CardTitle>
            <CardDescription>
              Jou verdienste oor tyd - vanaf jou eerste boekverkope
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 12 }}
                    tickLine={{ stroke: '#d1d5db' }}
                  />
                  <YAxis 
                    tickFormatter={(value) => `R${value}`}
                    tick={{ fontSize: 12 }}
                    tickLine={{ stroke: '#d1d5db' }}
                  />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'verdienste') return [`R${value.toFixed(2)}`, 'Jou Verdienste'];
                      if (name === 'totaal') return [`R${value.toFixed(2)}`, 'Totale Inkomste'];
                      return [value, name];
                    }}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Legend 
                    formatter={(value) => {
                      if (value === 'verdienste') return 'Jou Verdienste';
                      if (value === 'totaal') return 'Totale Inkomste';
                      return value;
                    }}
                  />
                  <Bar 
                    dataKey="verdienste" 
                    fill="#22c55e" 
                    radius={[4, 4, 0, 0]}
                    name="verdienste"
                  />
                  <Bar 
                    dataKey="totaal" 
                    fill="#f97316" 
                    radius={[4, 4, 0, 0]}
                    opacity={0.3}
                    name="totaal"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Chart Legend Explanation */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
              <p>
                <span className="inline-block w-3 h-3 bg-green-500 rounded mr-2"></span>
                <strong>Jou Verdienste</strong> - Die bedrag wat jy ontvang ({earningsData?.settings?.writer_pct || 60}% van totaal)
              </p>
              <p className="mt-1">
                <span className="inline-block w-3 h-3 bg-orange-500 opacity-30 rounded mr-2"></span>
                <strong>Totale Inkomste</strong> - Die volle bedrag voor verdeling
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MyEarnings;
